// ============================================================================
// EditPhoneOrderItemsModal — PR2 (telefon siparişi sepet düzenleme)
// ============================================================================
// Kullanım: PackageOrderScreen tarafından açılır. Yalnızca telefon siparişleri
// için ve PR0 `canEditPhoneOrderItems` guard'ından geçince render edilir.
//
// Sorumluluklar:
//  - Modal açılırken ilgili siparişin sepetini `getPackageOrderById` ile çeker.
//  - Adet stepper (+/-) → debounce 300ms → `updatePackageOrderItem`.
//  - Not inline → blur'da debounce 600ms → `updatePackageOrderItem`.
//  - Sil ikonu → onay popover → `removePackageOrderItem`.
//  - "Yeni Ürün Ekle" → parent'a callback (parent QuickSale ilave-sipariş modunu açar).
//  - Optimistic update + rollback + Snackbar.
//
// İletişim:
//  - `onMutated` her başarılı mutation'da çağrılır (parent listeyi tazeleyebilir).
//  - `onClose` modal kapatma; aktif istek varsa disable kalır.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import {
  getPackageOrderById,
  removePackageOrderItem,
  updatePackageOrderItem,
  PackageOrderDto,
  PackageOrderItemDto,
} from '../../../../services/salesService';
import { useAuth } from '../../../../contexts/AuthContext';

// ----------------------------------------------------------------------------
// Sabitler
// ----------------------------------------------------------------------------

const QUANTITY_DEBOUNCE_MS = 300;
const NOTE_DEBOUNCE_MS = 600;
const MAX_QUANTITY = 999;
const MIN_QUANTITY = 1;

// ----------------------------------------------------------------------------
// Tipler
// ----------------------------------------------------------------------------

export interface EditPhoneOrderItemsModalProps {
  open: boolean;
  /** Düzenlenecek siparişin id'si. null → modal render etmez. */
  orderId: number | null;
  /** Görsel başlık için (id'nin yanına yazdırılır). */
  orderShortNo?: string | null;
  /** Görsel başlık için (müşteri adı). */
  customerName?: string | null;
  onClose: () => void;
  /**
   * Her başarılı sepet mutation'ından sonra çağrılır.
   * Parent (PackageOrderScreen) genelde `loadPackageOrders` + selectedOrder
   * tazeleme yapar.
   */
  onMutated?: () => void;
  /**
   * "Yeni Ürün Ekle" butonu için. Parent QuickSale ilave-sipariş modunu açar.
   * Tanımlıysa buton görünür; yoksa hint gösterilir.
   */
  onAddNewItem?: (orderId: number) => void;
}

// ----------------------------------------------------------------------------
// Yardımcı: stable debounce ref (lodash bağımlılığı yok)
// ----------------------------------------------------------------------------

function useDebouncedCallback<T extends (...args: any[]) => void>(fn: T, delay: number) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const flush = useCallback((...args: Parameters<T>) => {
    cancel();
    fnRef.current(...args);
  }, [cancel]);
  const debounced = useCallback((...args: Parameters<T>) => {
    cancel();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      fnRef.current(...args);
    }, delay);
  }, [cancel, delay]);
  useEffect(() => () => cancel(), [cancel]);
  return { debounced, cancel, flush };
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

const EditPhoneOrderItemsModal: React.FC<EditPhoneOrderItemsModalProps> = ({
  open,
  orderId,
  orderShortNo,
  customerName,
  onClose,
  onMutated,
  onAddNewItem,
}) => {
  const { t } = useTranslation('salesSystem');
  const { userDetails, currentUser } = useAuth();
  const companyId = userDetails?.companyId;
  const updatedBy = currentUser?.uid;
  const updatedByName = useMemo(() => {
    if (userDetails?.firstName && userDetails?.lastName) {
      return `${userDetails.firstName} ${userDetails.lastName}`;
    }
    return currentUser?.email?.split('@')[0];
  }, [userDetails, currentUser]);

  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<PackageOrderDto | null>(null);
  const [items, setItems] = useState<PackageOrderItemDto[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyItemId, setBusyItemId] = useState<number | null>(null);

  // Sil onay popover state
  const [removePopover, setRemovePopover] = useState<{ anchor: HTMLElement; item: PackageOrderItemDto } | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Not düzenleme state'i (her satır için kendi local input)
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});
  // Hangi satır not düzenleme açık?
  const [noteEditingId, setNoteEditingId] = useState<number | null>(null);

  // Sipariş yüklenince items + note draft'lar set edilir.
  // companyId backend tarafında X-Company-Id header üzerinden uygulandığı için
  // burada explicit geçilmez; sadece guard amaçlı (auth context yok ise iptal).
  const loadOrder = useCallback(async () => {
    if (!orderId || !companyId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getPackageOrderById(orderId);
      if (!result.Success || !result.Data) {
        throw new Error(result.Message || 'Sipariş bulunamadı.');
      }
      const dto = result.Data;
      setOrder(dto);
      const list: PackageOrderItemDto[] = (dto.Items || []).slice().sort(
        (a: PackageOrderItemDto, b: PackageOrderItemDto) => a.Id - b.Id,
      );
      setItems(list);
      const drafts: Record<number, string> = {};
      list.forEach((it) => { drafts[it.Id] = it.Notes || ''; });
      setNoteDrafts(drafts);
    } catch (err: any) {
      const msg = err?.message || 'Sipariş yüklenemedi.';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [orderId, companyId]);

  // Modal her açılışta sipariş tazele.
  useEffect(() => {
    if (open && orderId) {
      loadOrder();
    }
    if (!open) {
      // Kapatma sırasında state'i temizle ki sonraki açılışta eski veri flash etmesin.
      setOrder(null);
      setItems([]);
      setNoteDrafts({});
      setNoteEditingId(null);
      setLoadError(null);
      setBusyItemId(null);
      setRemovePopover(null);
    }
  }, [open, orderId, loadOrder]);

  // ---- Mutation helpers ----

  const showError = useCallback((err: any) => {
    const msg = err?.message || String(err) || 'Bilinmeyen hata';
    setSnackbar({
      open: true,
      message: t('orderManagement.editPhoneOrderItems.snackbar.errorMutate', { error: msg }),
      severity: 'error',
    });
  }, [t]);

  const showSuccess = useCallback((key: 'successUpdate' | 'successRemove') => {
    setSnackbar({
      open: true,
      message: t(`orderManagement.editPhoneOrderItems.snackbar.${key}`),
      severity: 'success',
    });
  }, [t]);

  /**
   * Optimistic update + rollback için yardımcı.
   * Önce snapshot al, sonra UI'ı patch et, sonra API çağır.
   * Hata olursa snapshot'a dön.
   */
  const runOptimisticUpdate = useCallback(async (
    itemId: number,
    optimisticPatch: (prev: PackageOrderItemDto[]) => PackageOrderItemDto[],
    apiCall: () => Promise<PackageOrderDto>,
    successKey: 'successUpdate' | 'successRemove' = 'successUpdate',
  ) => {
    const snapshot = items;
    setBusyItemId(itemId);
    try {
      setItems((prev) => optimisticPatch(prev));
      const updatedOrder = await apiCall();
      const list: PackageOrderItemDto[] = (updatedOrder.Items || []).slice().sort(
        (a: PackageOrderItemDto, b: PackageOrderItemDto) => a.Id - b.Id,
      );
      setItems(list);
      setOrder(updatedOrder);
      // Note draft'ları server tarafından gelen değerle senkronla.
      setNoteDrafts((prev) => {
        const next = { ...prev };
        list.forEach((it) => {
          if (next[it.Id] === undefined) next[it.Id] = it.Notes || '';
        });
        return next;
      });
      showSuccess(successKey);
      onMutated?.();
    } catch (err) {
      setItems(snapshot);
      showError(err);
    } finally {
      setBusyItemId(null);
    }
  }, [items, onMutated, showError, showSuccess]);

  // ---- Quantity stepper (debounced) ----

  // Pending quantity per item — debounce sırasında UI'ya yansır.
  const lastSentQtyRef = useRef<Record<number, number>>({});

  const sendQuantity = useCallback((itemId: number, newQty: number) => {
    if (!orderId) return;
    if (lastSentQtyRef.current[itemId] === newQty) return;
    lastSentQtyRef.current[itemId] = newQty;
    runOptimisticUpdate(
      itemId,
      (prev) => prev.map((it) => it.Id === itemId
        ? { ...it, Quantity: newQty, TotalAmount: (it.UnitPrice || 0) * newQty }
        : it),
      () => updatePackageOrderItem(orderId, itemId, { quantity: newQty }, updatedBy, updatedByName),
    );
  }, [orderId, runOptimisticUpdate, updatedBy, updatedByName]);

  const { debounced: debouncedSendQuantity, cancel: cancelQtyDebounce } =
    useDebouncedCallback(sendQuantity, QUANTITY_DEBOUNCE_MS);

  const handleStep = useCallback((itemId: number, delta: number) => {
    setItems((prev) => prev.map((it) => {
      if (it.Id !== itemId) return it;
      const next = Math.max(MIN_QUANTITY, Math.min(MAX_QUANTITY, (it.Quantity || 1) + delta));
      if (next === it.Quantity) return it;
      // Optimistic local update (debounce bekliyor)
      debouncedSendQuantity(itemId, next);
      return { ...it, Quantity: next, TotalAmount: (it.UnitPrice || 0) * next };
    }));
  }, [debouncedSendQuantity]);

  // ---- Note (debounced on blur change) ----

  const sendNote = useCallback((itemId: number, newNote: string) => {
    if (!orderId) return;
    runOptimisticUpdate(
      itemId,
      (prev) => prev.map((it) => it.Id === itemId ? { ...it, Notes: newNote } : it),
      () => updatePackageOrderItem(orderId, itemId, { note: newNote }, updatedBy, updatedByName),
    );
  }, [orderId, runOptimisticUpdate, updatedBy, updatedByName]);

  const { debounced: debouncedSendNote, flush: flushSendNote } =
    useDebouncedCallback(sendNote, NOTE_DEBOUNCE_MS);

  const handleNoteChange = useCallback((itemId: number, value: string) => {
    setNoteDrafts((prev) => ({ ...prev, [itemId]: value }));
    debouncedSendNote(itemId, value);
  }, [debouncedSendNote]);

  const handleNoteBlur = useCallback((itemId: number) => {
    const draft = noteDrafts[itemId] ?? '';
    const original = items.find((it) => it.Id === itemId)?.Notes || '';
    if (draft !== original) {
      flushSendNote(itemId, draft);
    }
    setNoteEditingId(null);
  }, [noteDrafts, items, flushSendNote]);

  // ---- Remove (with confirm popover) ----

  const handleRemoveConfirm = useCallback(async () => {
    if (!removePopover || !orderId) return;
    const itemId = removePopover.item.Id;
    setRemovePopover(null);
    cancelQtyDebounce();
    runOptimisticUpdate(
      itemId,
      (prev) => prev.filter((it) => it.Id !== itemId),
      () => removePackageOrderItem(orderId, itemId, updatedBy, updatedByName),
      'successRemove',
    );
  }, [removePopover, orderId, runOptimisticUpdate, cancelQtyDebounce, updatedBy, updatedByName]);

  // ---- Computed ----

  const computedTotal = useMemo(() => items.reduce((sum, it) => sum + (it.TotalAmount || 0), 0), [items]);
  const isLastItem = items.length <= 1;
  const headerLabel = useMemo(() => {
    const no = orderShortNo || (orderId ? String(orderId) : '');
    return customerName
      ? t('orderManagement.editPhoneOrderItems.summaryWithCustomer', { no, customer: customerName })
      : t('orderManagement.editPhoneOrderItems.summary', { no });
  }, [orderShortNo, orderId, customerName, t]);

  const handleAddNew = useCallback(() => {
    if (!orderId || !onAddNewItem) return;
    onAddNewItem(orderId);
  }, [orderId, onAddNewItem]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={busyItemId == null ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" component="div" fontWeight={700}>
            {t('orderManagement.editPhoneOrderItems.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {headerLabel}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={busyItemId != null} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 320 }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && loadError && (
          <Alert severity="error" sx={{ my: 2 }}>{loadError}</Alert>
        )}

        {!loading && !loadError && items.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>{t('orderManagement.editPhoneOrderItems.noItems')}</Typography>
          </Box>
        )}

        {!loading && !loadError && items.length > 0 && (
          <Box>
            {items.map((it) => {
              const isBusy = busyItemId === it.Id;
              const isNoteEditing = noteEditingId === it.Id;
              const draft = noteDrafts[it.Id] ?? '';
              return (
                <Box
                  key={it.Id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    py: 1.5,
                    px: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    opacity: isBusy ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap title={it.ProductName || ''}>
                        {it.ProductName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('orderManagement.editPhoneOrderItems.unitPriceLabel')}: {(it.UnitPrice || 0).toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Adet stepper */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleStep(it.Id, -1)}
                        disabled={isBusy || (it.Quantity || 1) <= MIN_QUANTITY}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{ width: 36, textAlign: 'center', fontWeight: 600 }}
                        aria-label={t('orderManagement.editPhoneOrderItems.quantityLabel')}
                      >
                        {it.Quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleStep(it.Id, +1)}
                        disabled={isBusy || (it.Quantity || 1) >= MAX_QUANTITY}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Satır toplamı */}
                    <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {(it.TotalAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Not iconu */}
                    <Tooltip title={t('orderManagement.editPhoneOrderItems.noteLabel')}>
                      <span>
                        <IconButton
                          size="small"
                          color={it.Notes ? 'primary' : 'default'}
                          onClick={() => setNoteEditingId(isNoteEditing ? null : it.Id)}
                          disabled={isBusy}
                        >
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Sil iconu */}
                    <Tooltip title={isLastItem ? t('orderManagement.editPhoneOrderItems.lastItemBlocked') : ''} arrow>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => setRemovePopover({ anchor: e.currentTarget, item: it })}
                          disabled={isBusy || isLastItem}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  {(isNoteEditing || it.Notes) && (
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      maxRows={3}
                      placeholder={t('orderManagement.editPhoneOrderItems.notePlaceholder')}
                      value={draft}
                      onChange={(e) => handleNoteChange(it.Id, e.target.value)}
                      onBlur={() => handleNoteBlur(it.Id)}
                      autoFocus={isNoteEditing && !it.Notes}
                      disabled={isBusy}
                      inputProps={{ maxLength: 500 }}
                      sx={{ ml: 0.5 }}
                    />
                  )}
                </Box>
              );
            })}

            {/* Sipariş özeti */}
            <Box sx={{ mt: 2, pt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('orderManagement.editPhoneOrderItems.totalLabel')}: {(order?.TotalAmount ?? computedTotal).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Sil onay popover */}
        <Popover
          open={Boolean(removePopover)}
          anchorEl={removePopover?.anchor}
          onClose={() => setRemovePopover(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, maxWidth: 320 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t('orderManagement.editPhoneOrderItems.removeConfirmTitle')}
            </Typography>
            <Box
              sx={{ mb: 1.5, fontSize: '0.875rem' }}
              dangerouslySetInnerHTML={{
                __html: t('orderManagement.editPhoneOrderItems.removeConfirmDescription', {
                  name: removePopover?.item.ProductName ?? '',
                }),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button size="small" onClick={() => setRemovePopover(null)}>
                {t('orderManagement.editPhoneOrderItems.removeConfirmNo')}
              </Button>
              <Button size="small" variant="contained" color="error" onClick={handleRemoveConfirm}>
                {t('orderManagement.editPhoneOrderItems.removeConfirmYes')}
              </Button>
            </Box>
          </Box>
        </Popover>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {onAddNewItem ? (
            <Button
              variant="contained"
              startIcon={<AddShoppingCartIcon />}
              onClick={handleAddNew}
              disabled={loading || busyItemId != null || !orderId}
            >
              {t('orderManagement.editPhoneOrderItems.addItem')}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('orderManagement.editPhoneOrderItems.addItemHint')}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {busyItemId != null && (
            <>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {t('orderManagement.editPhoneOrderItems.saving')}
              </Typography>
            </>
          )}
          <Button onClick={onClose} disabled={busyItemId != null}>
            {t('orderManagement.editPhoneOrderItems.close')}
          </Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Şu an kullanılmayan UI ipuçlarını ileride genişletmek için List/ListItem
          import'ları lint için kullanılır kalsın diye no-op kullanım: */}
      <List sx={{ display: 'none' }}>
        <ListItem>
          <ListItemText primary="" />
        </ListItem>
      </List>
      <Divider sx={{ display: 'none' }} />
    </Dialog>
  );
};

export default EditPhoneOrderItemsModal;
